import { motion } from "motion/react";
import React from "react";
import { UI_MOTION } from "../../../config/uiMotion.configuration";
import { useReducedMotion } from "../../../hooks/useReducedMotion.hook";
import { getUIColorPalette } from "../../../theme/ColorPalette";
import { isUiMotionEnabled, resolveUiEaseOut } from "../../shared/motion/uiMotion.tools";

interface TransitPageLoaderProps {

    /** Loader row DOM id. */
    "id": string;

    /** Loading label shown inside the pill. */
    "label": string;

    /** Whether dark UI palette is active. */
    "isDarkUi": boolean;
}

/**
 * Animated loader pill shown while a transit results page is fetched.
 *
 * @remarks Mirrors the transit page button silhouette (`rounded-full border-2`,
 * `brandPrimary` border/text on `selectorIdleBackground`) and carries a soft
 * brand-colored shimmer sweeping left to right — the button-to-loader swap reads
 * as a morph into a loading state. Fades/slides in and out unless reduced motion
 * is preferred (then the sweep is not rendered at all).
 *
 * @param props Loader identity, label, and palette flag.
 * @returns Centered animated loading pill.
 */
const TransitPageLoader: React.FC<TransitPageLoaderProps> = ( { id, label, isDarkUi } ) => {
    const palette = getUIColorPalette( isDarkUi );
    const reducedMotion = useReducedMotion();
    const animationsEnabled = isUiMotionEnabled( reducedMotion );

    return (
        <motion.div
            animate={{
                "opacity": 1,
                "y":       0,
            }}
            className="relative inline-flex items-center justify-center overflow-hidden rounded-full border-2 border-solid px-3.5 py-1.5 text-xs font-bold shadow-sm"
            exit={animationsEnabled
                ? {
                    "opacity": 0,
                    "y":       -6,
                }
                : undefined}
            id={id}
            initial={animationsEnabled
                ? {
                    "opacity": 0,
                    "y":       6,
                }
                : false}
            style={{
                "borderColor":     palette.brandPrimary,
                "backgroundColor": palette.selectorIdleBackground,
                "color":           palette.brandPrimary,
            }}
            transition={resolveUiEaseOut( reducedMotion, UI_MOTION.duration.fastMs )}
        >
            {animationsEnabled && (
                <motion.span
                    animate={{ "left": [ "-45%", "145%" ] }}
                    aria-hidden={true}
                    className="pointer-events-none absolute inset-y-0"
                    style={{
                        "background": `linear-gradient(90deg, transparent, ${ palette.brandPrimary }38, transparent)`,
                        "width":      "45%",
                    }}
                    transition={{
                        "duration": 1.4,
                        "ease":     "easeInOut",
                        "repeat":   Infinity,
                    }}
                />
            )}
            {label}
        </motion.div>
    );
};

export default TransitPageLoader;
